import React, { FC, useState } from 'react'
import { Button, Modal, Typography, Row, Col } from 'antd'
import { DownloadOutlined, DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { AttachmentListProps, AttachmentRecord } from './types'
import styles from './styles.module.scss'
import { MESSAGES } from './constants'

export const AttachmentList: FC<AttachmentListProps> = ({
  attachments,
  currentUserId,
  title,
  onDownload,
  onRemove
}) => {
  const [pendingAttachment, setPendingAttachment] = useState<AttachmentRecord | null>(null)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [downloadingId, setDownloadingId] = useState<number | null>(null)

  if (attachments.length === 0) return null

  const handleOk = async () => {
    if (!pendingAttachment || !onRemove) return

    setConfirmLoading(true)
    await onRemove(pendingAttachment)
    setConfirmLoading(false)
    setPendingAttachment(null)
  }

  return (
    <div className={styles.attachmentList}>
      <Typography.Text strong>{title ?? MESSAGES.UPLOADED_DOCUMENTS_TITLE}</Typography.Text>
      <div className={styles.attachmentListItems}>
        {attachments.map((attachment: AttachmentRecord) => (
          <Row
            key={attachment.id}
            className={styles.attachmentCard}
            justify="space-between"
            align="middle"
            wrap={false}
          >
            <Col>
              <Typography.Text strong>{attachment.name}</Typography.Text>
              <Typography.Text type="secondary" className={styles.attachmentMeta}>
                {attachment.fileSize} {MESSAGES.UPLOADED_ON_PREFIX} {attachment.createdAt}
              </Typography.Text>
            </Col>
            <Col flex="none" className={styles.attachmentActions}>
              {onDownload && (
                <Button
                  icon={<DownloadOutlined />}
                  size="small"
                  loading={downloadingId === attachment.id}
                  onClick={async () => {
                    setDownloadingId(attachment.id)
                    await onDownload(attachment)
                    setDownloadingId(null)
                  }}
                />
              )}
              {onRemove && currentUserId === attachment.uploadedById && (
                <Button icon={<DeleteOutlined />} size="small" onClick={() => setPendingAttachment(attachment)} />
              )}
            </Col>
          </Row>
        ))}
      </div>
      <Modal open={pendingAttachment !== null} width={350} closable={false} footer={null}>
        <Row gutter={8} wrap={false}>
          <Col flex="none">
            <Typography.Title level={5}>
              <ExclamationCircleOutlined className={styles.warningIcon} />
            </Typography.Title>
          </Col>
          <Col flex="auto">
            <Typography.Title level={5}>{MESSAGES.DELETE_DOCUMENT_TITLE}</Typography.Title>
            <Typography.Paragraph>{MESSAGES.DELETE_DOCUMENT_CONFIRM}</Typography.Paragraph>
            <Row justify="end" gutter={8}>
              <Col>
                <Button onClick={() => setPendingAttachment(null)}>{MESSAGES.CANCEL_BUTTON_LABEL}</Button>
              </Col>
              <Col>
                <Button type="primary" loading={confirmLoading} onClick={handleOk}>
                  {MESSAGES.DELETE_BUTTON_LABEL}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Modal>
    </div>
  )
}
